import React, { useState, useRef, useEffect } from 'react';
import { Upload, Video, Search, Ticket, Save, Eye, EyeOff, Copy, Trash2, Plus, Settings } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface PromoCode {
  id: string;
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  description: string;
  expiryDate: string;
  isActive: boolean;
  usageCount: number;
  maxUsage: number;
}

interface SEOSettings {
  title: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
}

interface VideoSettings {
  url: string;
  isActive: boolean;
}

const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'video' | 'seo' | 'promo'>('video');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<VideoSettings>({ url: '', isActive: false });
  
  // SEO 设置状态
  const [seoSettings, setSeoSettings] = useState<SEOSettings>({
    title: 'Reddit 热点看板 - 智能内容聚合平台',
    description: '实时追踪Reddit热门话题，智能生成微信公众号文章和播客内容。助力内容创作者快速捕捉网络热点。',
    keywords: 'Reddit, 热点, AI生成, 微信公众号, 播客, 内容创作',
    ogTitle: '',
    ogDescription: '',
    ogImage: ''
  });

  // 促销码状态
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [newPromoCode, setNewPromoCode] = useState<Partial<PromoCode>>({
    code: '',
    discount: 0,
    type: 'percentage',
    description: '',
    expiryDate: '',
    maxUsage: 100
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 加载数据
  useEffect(() => {
    loadCurrentVideo();
    loadSEOSettings();
    loadPromoCodes();
  }, []);

  // 加载当前视频设置
  const loadCurrentVideo = async () => {
    try {
      const response = await fetch('/api/admin/current-video');
      if (response.ok) {
        const video = await response.json();
        setCurrentVideo(video);
      }
    } catch (error) {
      console.error('Failed to load video settings:', error);
    }
  };

  // 加载SEO设置
  const loadSEOSettings = async () => {
    try {
      const response = await fetch('/api/admin/seo-settings');
      if (response.ok) {
        const settings = await response.json();
        setSeoSettings(settings);
      }
    } catch (error) {
      console.error('Failed to load SEO settings:', error);
    }
  };

  // 加载促销码
  const loadPromoCodes = async () => {
    try {
      const response = await fetch('/api/admin/promo-codes');
      if (response.ok) {
        const codes = await response.json();
        setPromoCodes(codes);
      }
    } catch (error) {
      console.error('Failed to load promo codes:', error);
    }
  };

  // 视频上传处理
  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 检查文件类型
      if (!file.type.startsWith('video/')) {
        toast.error('请选择视频文件');
        return;
      }
      
      // 检查文件大小 (限制为50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('视频文件不能超过50MB');
        return;
      }

      setVideoFile(file);
      
      // 创建预览
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
      
      toast.success('视频选择成功');
    }
  };

  const handleVideoUpload = async () => {
    if (!videoFile) {
      toast.error('请先选择视频文件');
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      
      const response = await fetch('/api/admin/upload-video', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('视频上传成功！');
        
        // 清理状态
        setVideoFile(null);
        setVideoPreview('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // 重新加载视频设置
        loadCurrentVideo();
      } else {
        throw new Error('上传失败');
      }
    } catch (error) {
      toast.error('视频上传失败，请重试');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // 切换视频显示状态
  const toggleVideoDisplay = async () => {
    try {
      const response = await fetch('/api/admin/toggle-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isActive: !currentVideo.isActive }),
      });

      if (response.ok) {
        setCurrentVideo(prev => ({ ...prev, isActive: !prev.isActive }));
        toast.success(currentVideo.isActive ? '视频已隐藏' : '视频已显示');
      } else {
        throw new Error('切换失败');
      }
    } catch (error) {
      toast.error('操作失败，请重试');
      console.error('Toggle error:', error);
    }
  };

  // SEO设置保存
  const handleSeoSave = async () => {
    try {
      const response = await fetch('/api/admin/seo-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(seoSettings),
      });

      if (response.ok) {
        toast.success('SEO设置保存成功！');
      } else {
        throw new Error('保存失败');
      }
    } catch (error) {
      toast.error('SEO设置保存失败');
      console.error('SEO save error:', error);
    }
  };

  // 促销码管理
  const handleAddPromoCode = async () => {
    if (!newPromoCode.code || !newPromoCode.description) {
      toast.error('请填写完整信息');
      return;
    }

    try {
      const response = await fetch('/api/admin/promo-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPromoCode),
      });

      if (response.ok) {
        const promoCode = await response.json();
        setPromoCodes([...promoCodes, promoCode]);
        setNewPromoCode({
          code: '',
          discount: 0,
          type: 'percentage',
          description: '',
          expiryDate: '',
          maxUsage: 100
        });
        toast.success('促销码添加成功！');
      } else {
        throw new Error('添加失败');
      }
    } catch (error) {
      toast.error('促销码添加失败');
      console.error('Add promo code error:', error);
    }
  };

  const togglePromoCodeStatus = async (id: string) => {
    try {
      const code = promoCodes.find(c => c.id === id);
      if (!code) return;

      const response = await fetch(`/api/admin/promo-codes/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...code, isActive: !code.isActive }),
      });

      if (response.ok) {
        setPromoCodes(promoCodes.map(code => 
          code.id === id ? { ...code, isActive: !code.isActive } : code
        ));
        toast.success('状态更新成功');
      } else {
        throw new Error('更新失败');
      }
    } catch (error) {
      toast.error('状态更新失败');
      console.error('Toggle promo code error:', error);
    }
  };

  const deletePromoCode = async (id: string) => {
    if (!confirm('确定要删除这个促销码吗？')) return;

    try {
      const response = await fetch(`/api/admin/promo-codes/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setPromoCodes(promoCodes.filter(code => code.id !== id));
        toast.success('促销码删除成功');
      } else {
        throw new Error('删除失败');
      }
    } catch (error) {
      toast.error('删除失败');
      console.error('Delete promo code error:', error);
    }
  };

  const copyPromoCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('促销码已复制到剪贴板');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 页面标题 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center">
            <Settings className="h-8 w-8 text-primary mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">管理后台</h1>
              <p className="text-gray-600 mt-1">管理网站内容、SEO设置和促销活动</p>
            </div>
          </div>
        </div>

        {/* 标签导航 */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('video')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'video'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Video className="w-5 h-5 inline mr-2" />
                悬浮视频管理
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'seo'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Search className="w-5 h-5 inline mr-2" />
                SEO优化
              </button>
              <button
                onClick={() => setActiveTab('promo')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'promo'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Ticket className="w-5 h-5 inline mr-2" />
                促销码管理
              </button>
            </nav>
          </div>
        </div>

        {/* 标签内容 */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* 视频管理标签 */}
          {activeTab === 'video' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">悬浮视频管理</h2>
                {currentVideo.url && (
                  <button
                    onClick={toggleVideoDisplay}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                      currentVideo.isActive
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {currentVideo.isActive ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    {currentVideo.isActive ? '隐藏视频' : '显示视频'}
                  </button>
                )}
              </div>

              {/* 当前视频状态 */}
              {currentVideo.url && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">当前视频状态</h3>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">视频地址: {currentVideo.url}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      currentVideo.isActive 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {currentVideo.isActive ? '显示中' : '已隐藏'}
                    </span>
                  </div>
                </div>
              )}

              {/* 视频上传区域 */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <label htmlFor="video-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        上传新的悬浮视频
                      </span>
                      <span className="mt-1 block text-sm text-gray-500">
                        支持 MP4, WebM, AVI 格式，最大 50MB
                      </span>
                    </label>
                    <input
                      ref={fileInputRef}
                      id="video-upload"
                      name="video-upload"
                      type="file"
                      accept="video/*"
                      className="sr-only"
                      onChange={handleVideoSelect}
                    />
                  </div>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                    >
                      选择视频文件
                    </button>
                  </div>
                </div>
              </div>

              {/* 视频预览和上传 */}
              {videoPreview && (
                <div className="space-y-4">
                  <div className="bg-black rounded-lg overflow-hidden">
                    <video 
                      src={videoPreview} 
                      controls 
                      className="w-full max-h-64"
                      style={{ maxWidth: '400px', margin: '0 auto', display: 'block' }}
                    />
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={handleVideoUpload}
                      disabled={isUploading}
                      className={`flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                        isUploading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {isUploading ? '上传中...' : '确认上传'}
                    </button>
                    <button
                      onClick={() => {
                        setVideoFile(null);
                        setVideoPreview('');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      取消
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SEO优化标签 */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">SEO优化设置</h2>
                <button
                  onClick={handleSeoSave}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                >
                  <Save className="w-4 h-4 mr-2" />
                  保存设置
                </button>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {/* 基础SEO设置 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">基础SEO信息</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      页面标题
                    </label>
                    <input
                      type="text"
                      value={seoSettings.title}
                      onChange={(e) => setSeoSettings(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="网站主标题"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      页面描述
                    </label>
                    <textarea
                      rows={3}
                      value={seoSettings.description}
                      onChange={(e) => setSeoSettings(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="网站描述，用于搜索引擎展示"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      关键词
                    </label>
                    <input
                      type="text"
                      value={seoSettings.keywords}
                      onChange={(e) => setSeoSettings(prev => ({ ...prev, keywords: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="关键词，用逗号分隔"
                    />
                  </div>
                </div>

                {/* Open Graph设置 */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">社交媒体分享 (Open Graph)</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OG标题
                    </label>
                    <input
                      type="text"
                      value={seoSettings.ogTitle}
                      onChange={(e) => setSeoSettings(prev => ({ ...prev, ogTitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="社交媒体分享时显示的标题"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OG描述
                    </label>
                    <textarea
                      rows={3}
                      value={seoSettings.ogDescription}
                      onChange={(e) => setSeoSettings(prev => ({ ...prev, ogDescription: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="社交媒体分享时显示的描述"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OG图片URL
                    </label>
                    <input
                      type="url"
                      value={seoSettings.ogImage}
                      onChange={(e) => setSeoSettings(prev => ({ ...prev, ogImage: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 促销码管理标签 */}
          {activeTab === 'promo' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">促销码管理</h2>
              </div>

              {/* 添加新促销码 */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">添加新促销码</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      促销码
                    </label>
                    <input
                      type="text"
                      value={newPromoCode.code}
                      onChange={(e) => setNewPromoCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="PROMO20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      折扣类型
                    </label>
                    <select
                      value={newPromoCode.type}
                      onChange={(e) => setNewPromoCode(prev => ({ ...prev, type: e.target.value as 'percentage' | 'fixed' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="percentage">百分比</option>
                      <option value="fixed">固定金额</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      折扣值
                    </label>
                    <input
                      type="number"
                      value={newPromoCode.discount}
                      onChange={(e) => setNewPromoCode(prev => ({ ...prev, discount: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="20"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      过期日期
                    </label>
                    <input
                      type="date"
                      value={newPromoCode.expiryDate}
                      onChange={(e) => setNewPromoCode(prev => ({ ...prev, expiryDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      描述
                    </label>
                    <input
                      type="text"
                      value={newPromoCode.description}
                      onChange={(e) => setNewPromoCode(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="促销活动描述"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      使用次数限制
                    </label>
                    <input
                      type="number"
                      value={newPromoCode.maxUsage}
                      onChange={(e) => setNewPromoCode(prev => ({ ...prev, maxUsage: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="100"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={handleAddPromoCode}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      添加促销码
                    </button>
                  </div>
                </div>
              </div>

              {/* 促销码列表 */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">现有促销码</h3>
                {promoCodes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    暂无促销码
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {promoCodes.map((code) => (
                      <div
                        key={code.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-4">
                              <span className="text-lg font-bold text-primary">{code.code}</span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                code.isActive 
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {code.isActive ? '启用' : '禁用'}
                              </span>
                              <span className="text-sm text-gray-600">
                                {code.type === 'percentage' ? `${code.discount}% 折扣` : `￥${code.discount} 减免`}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              <p>{code.description}</p>
                              <p>过期时间: {code.expiryDate} | 已使用: {code.usageCount}/{code.maxUsage}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => copyPromoCode(code.code)}
                              className="p-2 text-gray-400 hover:text-gray-600"
                              title="复制促销码"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => togglePromoCodeStatus(code.id)}
                              className={`p-2 rounded ${
                                code.isActive 
                                  ? 'text-red-600 hover:bg-red-50'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={code.isActive ? '禁用' : '启用'}
                            >
                              {code.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => deletePromoCode(code.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="删除"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel; 